<template>
  <!--
    WB-M1A -- Persistent Board Core (doc 23 §13.4): the Workspace of ONE AI
    Tutor conversation. The Borna-owned board, persisted in Subway, beside the
    existing tutor chat.

    Two independent halves:
      - the BOARD is loaded and saved through the `aiTutorBoard` store, which
        talks only to Subway. It never waits for the chat, and it stays usable
        when the tutor upstream is not (WB-M1-D13);
      - the CHAT panel is the accepted chat: the same `aiTutor` store session,
        `TurnList`, `Composer`, stream, feedback, completion flow and Credits
        V1 behaviour as the chat route. If it cannot be opened it shows a
        bounded "unavailable" state; the board is unaffected.

    The shell is the accepted Student shell: `student-app-header` with the
    credit pill, the sidebar, and the mobile bottom navigation. No immersive
    mode. Layout is functional guidance from DESIGN-21, not a visually frozen
    reference.

    Not here in M1A: Send-to-Tutor, media, equations, AI patches. Editing the
    board consumes no credits and is never gated by them.

    WB-M1B S4 (doc 23 §23): the Equation. ƒx on the toolbar opens the Equation
    editor (BoardEquationDialog) -- on the one selected equation if there is
    one, else for a new one; «ویرایش فرمول» in the board's footer and a
    double-click on an equation open the same editor. The Workspace only
    passes the submitted source to the board editor (`insertEquation`,
    `updateEquation`); what is saved, and when, is the board's own autosave.
    S4.1 (§25): an EDIT submitted with the source it was opened on is not an
    edit -- the board editor is not called at all.

    WB-M1B S6 (doc 23 §29): polish only. An empty board says what it is for;
    the chat panel keeps a readable width beside the board; the board's bar
    keeps its controls inside the card on a phone.

    WB-M2A (doc 31): Board → AI Tutor برنا, read-only. «از AI Tutor بپرس» on
    the toolbar renders a PICTURE of the selection (or of the visible board)
    through the board editor, freezes it with the board's identity and
    version, and opens the share sheet (BoardShareDialog) on exactly that
    picture. Sending flushes the board first -- a stale board blocks the
    send; a transient save failure does not -- and then hands the frozen
    picture, the Student's visible message and the Borna-only share
    descriptor to the ordinary chat send (`aiTutor/sendMessage`): the same
    upload, the same turn, the same transcription confirmation a photo gets.
    A retry sends the same frozen picture. The Tutor never reads, requests or
    changes the board.
  -->
  <div class="ai-tutor-board-view" data-testid="ai-tutor-board-view">
    <student-app-header
      :title="$t('aiTutorBoard.headerTitle')"
      :ai-tutor-credits-remaining="aiTutorCreditsRemaining"
    />

    <div
      class="ai-tutor-board"
      :class="{
        'ai-tutor-board--chat-collapsed': !sheetMode && !chatOpen,
        'ai-tutor-board--sheet': sheetMode,
      }"
    >
      <section class="ai-tutor-board__board" data-testid="ai-tutor-board-board">
        <header class="ai-tutor-board__bar">
          <button
            type="button"
            class="ai-tutor-board__icon-button"
            data-testid="ai-tutor-board-back"
            :aria-label="$t('aiTutorBoard.backToReports')"
            :title="$t('aiTutorBoard.backToReports')"
            @click="goToReports(false)"
          >
            <student-home-icon name="arrow" :size="18" />
          </button>
          <!-- P43-03: a topic that carries math (`$…$` or `\(…\)`) is typeset by
               the chat's own safe renderer, isolated from the surrounding
               text's direction; every other title stays the one plain text node
               it was. -->
          <h1 class="ai-tutor-board__title" data-testid="ai-tutor-board-title">
            <template v-if="topicHasMath"
              >{{ $t('aiTutorBoard.title') }} ·
              <bdi class="ai-tutor-board__title-math"
                ><markdown-content inline :source="topicLabel"/></bdi
            ></template>
            <template v-else>{{ boardTitle }}</template>
          </h1>
          <span
            v-if="boardStatus === 'ready' && saveState !== 'clean'"
            class="ai-tutor-board__save"
            :class="`ai-tutor-board__save--${saveState}`"
            role="status"
            data-testid="ai-tutor-board-save-state"
            :data-save-state="saveState"
          >
            {{ saveLabel }}
          </span>
          <button
            v-if="!chatOpen"
            type="button"
            class="ai-tutor-board__chat-toggle"
            data-testid="ai-tutor-board-chat-open"
            @click="openChat"
          >
            <student-home-icon name="chat" :size="18" />
            <span>{{ $t('aiTutorBoard.chat.open') }}</span>
            <i
              v-if="unread"
              class="ai-tutor-board__unread"
              aria-hidden="true"
            ></i>
          </button>
        </header>

        <div
          v-if="boardStatus !== 'ready'"
          class="ai-tutor-board__state"
          :role="boardStatus === 'loading' ? 'status' : 'alert'"
          data-testid="ai-tutor-board-state"
          :data-board-status="boardStatus"
        >
          <p>{{ boardStateText }}</p>
          <button
            v-if="boardStatus === 'error'"
            type="button"
            class="ai-tutor-board__button"
            data-testid="ai-tutor-board-load-retry"
            @click="loadBoard"
          >
            {{ $t('aiTutorBoard.states.retry') }}
          </button>
          <button
            v-else-if="boardStatus !== 'loading' && boardStatus !== 'idle'"
            type="button"
            class="ai-tutor-board__button"
            data-testid="ai-tutor-board-state-back"
            @click="goToReports(false)"
          >
            {{ $t('aiTutorBoard.backToReports') }}
          </button>
        </div>

        <template v-else>
          <board-toolbar
            :active-tool="activeTool"
            :disabled="!editorReady || readOnly || editorFailed"
            :view-disabled="!editorReady"
            share
            :share-disabled="shareDisabled"
            @tool="onTool"
            @equation="onEquationTool"
            @undo="onUndo"
            @redo="onRedo"
            @reset-view="onResetView"
            @share="onShareTool"
            :pen-style="penStyle"
            :clear-disabled="boardEmpty"
            @pen-style="onPenStyle"
            @clear="onClearBoard"
          />
          <div class="ai-tutor-board__canvas">
            <board-editor
              ref="editor"
              :key="editorKey"
              :locale="locale"
              :initial-scene="initialScene"
              :read-only="readOnly"
              :pen-style="penStyle"
              @ready="onEditorReady"
              @change="onEditorChange"
              @equation-selection="onEquationSelection"
              @equation-edit="onEquationEdit"
              @chrome-leak="editorFailed = true"
            />
            <!-- WB-M1B S6: nothing on the board yet. Never in the way: the
                 pointer goes through it, and it leaves with the first mark. -->
            <div
              v-if="emptyHintShown"
              class="ai-tutor-board__empty"
              data-testid="ai-tutor-board-empty"
            >
              <strong>{{ $t('aiTutorBoard.empty.title') }}</strong>
              <span>{{ $t('aiTutorBoard.empty.body') }}</span>
            </div>
          </div>
          <footer class="ai-tutor-board__foot">
            <!-- P40 QA-28: leaving a Board that could not be saved asks HERE,
                 never with the browser's own confirm dialog. -->
            <div
              v-if="leavePrompt"
              class="ai-tutor-board__notice ai-tutor-board__notice--leave"
              role="alertdialog"
              :aria-label="leavePromptText"
              data-testid="ai-tutor-board-leave-prompt"
              @keydown.esc.stop.prevent="onLeaveStay"
            >
              <span>{{ leavePromptText }}</span>
              <button
                ref="leaveStay"
                type="button"
                class="ai-tutor-board__button"
                data-testid="ai-tutor-board-leave-stay"
                @click="onLeaveStay"
              >
                {{ $t('aiTutorBoard.leave.stay') }}
              </button>
              <button
                type="button"
                class="ai-tutor-board__button"
                data-testid="ai-tutor-board-leave-anyway"
                @click="onLeaveAnyway"
              >
                {{ $t('aiTutorBoard.leave.anyway') }}
              </button>
            </div>
            <!-- Doc 23 §13.4.8: a save that lost the version race. The board
                 is read-only until it is reloaded; nothing is merged and
                 nothing is overwritten. -->
            <div
              v-else-if="saveState === 'stale'"
              class="ai-tutor-board__notice ai-tutor-board__notice--stale"
              role="alert"
              data-testid="ai-tutor-board-stale"
            >
              <span>{{ $t('aiTutorBoard.save.staleHint') }}</span>
              <button
                type="button"
                class="ai-tutor-board__button"
                data-testid="ai-tutor-board-reload"
                :disabled="reloading"
                @click="onReload"
              >
                {{ $t('aiTutorBoard.save.reload') }}
              </button>
            </div>
            <!-- Doc 23 §13.4.9: retries exhausted (or the save was refused).
                 The failure stays on screen until the student retries. -->
            <div
              v-else-if="saveState === 'failed'"
              class="ai-tutor-board__notice ai-tutor-board__notice--failed"
              role="alert"
              data-testid="ai-tutor-board-not-saved"
            >
              <span>{{ notSavedText }}</span>
              <button
                type="button"
                class="ai-tutor-board__button"
                data-testid="ai-tutor-board-save-retry"
                @click="onRetrySave"
              >
                {{ $t('aiTutorBoard.save.retry') }}
              </button>
            </div>
            <span
              v-else-if="editorFailed"
              class="ai-tutor-board__notice ai-tutor-board__notice--failed"
              role="alert"
              data-testid="ai-tutor-board-editor-failed"
            >
              {{ $t('aiTutorBoard.states.editorFailed') }}
            </span>
            <span v-else class="ai-tutor-board__hint">
              {{ $t('aiTutorBoard.hint') }}
            </span>
            <!-- WB-M1B S4: the explicit edit action of the ONE selected
                 equation. It opens the editor ƒx opens for that equation. -->
            <button
              v-if="equationEditable"
              type="button"
              class="ai-tutor-board__button ai-tutor-board__equation-edit"
              data-testid="ai-tutor-board-equation-edit"
              @click="onEquationEdit(selectedEquationId)"
            >
              {{ $t('aiTutorBoard.equation.editAction') }}
            </button>
          </footer>
        </template>
      </section>

      <button
        v-if="sheetMode && chatOpen"
        type="button"
        class="ai-tutor-board__sheet-backdrop"
        :aria-label="$t('aiTutorBoard.chat.close')"
        @click="closeChat"
      ></button>

      <!-- `v-show`, never `v-if`: the conversation (and its scroll position)
           stays mounted whether the panel is open, collapsed or a sheet. -->
      <aside
        v-show="chatOpen"
        class="ai-tutor-board__chat"
        data-testid="ai-tutor-board-chat"
        :data-chat-status="chatStatus"
        :aria-label="$t('aiTutorBoard.chat.title')"
      >
        <header class="ai-tutor-board__chat-head">
          <span class="ai-tutor-board__chat-chip">
            <student-home-icon name="spark" :size="14" />
            {{ $t('aiTutorBoard.chat.title') }}
          </span>
          <strong class="ai-tutor-board__chat-topic"
            ><markdown-content
              v-if="topicHasMath"
              inline
              :source="topicLabel"
            />
            <template v-else>{{ topicLabel }}</template></strong
          >
          <button
            type="button"
            class="ai-tutor-board__icon-button"
            data-testid="ai-tutor-board-chat-close"
            :aria-label="$t('aiTutorBoard.chat.close')"
            :title="$t('aiTutorBoard.chat.close')"
            @click="closeChat"
          >
            <student-home-icon name="close" :size="17" />
          </button>
        </header>

        <div
          v-if="chatStatus === 'loading'"
          class="ai-tutor-board__chat-state"
          role="status"
          data-testid="ai-tutor-board-chat-loading"
        >
          {{ $t('aiTutorBoard.chat.loading') }}
        </div>
        <!-- The chat could not be opened (the tutor upstream is unreachable,
             or the conversation has no session). Bounded: the board beside it
             keeps working. -->
        <div
          v-else-if="chatStatus === 'unavailable'"
          class="ai-tutor-board__chat-state"
          role="status"
          data-testid="ai-tutor-board-chat-unavailable"
        >
          <p>{{ $t('aiTutorBoard.chat.unavailable') }}</p>
          <button
            type="button"
            class="ai-tutor-board__button"
            data-testid="ai-tutor-board-chat-retry"
            @click="loadChat"
          >
            {{ $t('aiTutorBoard.chat.retry') }}
          </button>
        </div>

        <template v-else-if="activeSession">
          <div class="ai-tutor-board__chat-scroll">
            <!-- Keyed by session, exactly as the chat route keys it. -->
            <turn-list
              ref="turnList"
              :key="activeSession.id"
              :turns="turns"
              :processing-state="processingState"
              :student-initial-source="studentInitialSource"
              :pending-turn="visiblePendingTurn"
              :awaiting-reply="awaitingTutorReply"
              :feedback-by-turn-id="feedbackByTurnId"
              :feedback-enabled="feedbackEnabled"
              :resend-disabled="composerDisabled"
              :media-source-by-turn-id="mediaSourceByTurnId"
              :board-share-by-turn-id="boardShareByTurnId"
              :actions-disabled="composerDisabled"
              :stream-status="streamStatus"
              @pick-suggestion="onPickSuggestion"
              @resend="onResend"
              @feedback="onFeedback"
              @dismiss-feedback="onDismissFeedback"
              @media-expired="onMediaExpired"
              @end-conversation="onEndConversation"
              @new-conversation="onNewQuestion"
            />
          </div>

          <p
            v-if="
              error && !creditsErrorShownAsWarning && !actionRecoveryPending
            "
            class="ai-tutor-board__chat-error"
            role="alert"
            data-testid="ai-tutor-board-chat-error"
          >
            {{ errorNote }}
          </p>

          <div class="ai-tutor-board__composer">
            <composer
              ref="composer"
              panel
              :disabled="composerDisabled"
              :first-question="turns.length === 0 && !visiblePendingTurn"
              :guided="clarifying"
              :voice="composerVoice"
              @submit="onComposerSubmit"
              @voice-start="onVoiceStart"
              @voice-stop="onVoiceStop"
              @voice-cancel="onVoiceCancel"
              @voice-dismiss="onVoiceDismiss"
            >
              <!-- CR-03: the zero-credit warning, exactly where the chat
                   route puts it. It gates chat sends only -- the board
                   beside it stays editable.
                   AT-API-020-H1 (doc 34/01 §3.3): the refused-action notice
                   shares that area, as on the chat route. -->
              <template
                v-if="creditsExhausted || actionRecoveryPending"
                #notice
              >
                <chat-credit-warning v-if="creditsExhausted" />
                <chat-action-recovery
                  v-if="actionRecoveryPending"
                  :disabled="composerDisabled"
                  @send-plain="onSendWithoutAction"
                  @dismiss="onDismissActionRecovery"
                />
              </template>
            </composer>
          </div>
        </template>
      </aside>
    </div>

    <board-equation-dialog
      v-if="equationDialog"
      :mode="equationDialog.mode"
      :source="equationDialog.source"
      :busy="equationDialog.busy"
      :problem="equationDialog.problem"
      @submit="onEquationSubmit"
      @edited="onEquationEdited"
      @cancel="closeEquationDialog"
    />
    <!-- WB-M2A: the share sheet, on the frozen picture. Modal: while it is
         open nothing reaches the board, so the preview is what is sent. -->
    <board-share-dialog
      v-if="shareDialog"
      :share="shareDialog.share"
      :busy="shareDialog.busy"
      :problem="shareDialog.problem || shareBlockedBy"
      @send="onShareSend"
      @cancel="closeShareDialog"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import StudentAppHeader from '@/components/StudentShell/StudentAppHeader.vue'
import TurnList from '@/components/AiTutor/Chat/TurnList.vue'
import Composer from '@/components/AiTutor/Chat/Composer.vue'
import ChatCreditWarning from '@/components/AiTutor/Chat/ChatCreditWarning.vue'
import ChatActionRecovery from '@/components/AiTutor/Chat/ChatActionRecovery.vue'
import StudentHomeIcon from '@/components/StudentHome/StudentHomeIcon.vue'
import MarkdownContent from '@/components/AiTutor/Chat/MarkdownContent.vue'
import BoardEditor from '@/components/AiTutor/Board/BoardEditor.vue'
import BoardToolbar from '@/components/AiTutor/Board/BoardToolbar.vue'
import { DEFAULT_PEN_STYLE } from '@/services/aiTutor/boardPen'
import BoardEquationDialog from '@/components/AiTutor/Board/BoardEquationDialog.vue'
import BoardShareDialog from '@/components/AiTutor/Board/BoardShareDialog.vue'
import { collaborators as aiTutorCollaborators } from '@/store/modules/aiTutor'
import composerVoice, {
  voiceCapabilityFrom,
} from '@/components/AiTutor/Chat/composerVoice'
import { freezeBoardShare } from '@/services/aiTutor/boardSnapshot'
import { isOnline, onReconnect } from '@/services/connectivity'
import {
  boardShareRequest,
  toBoardShareDescriptor,
} from '@/services/aiTutor/boardShare'
import {
  createPreviewUrl,
  revokePreviewUrl,
} from '@/services/aiTutor/imageFile'

// The chat route's own mapping (StudentAiTutorView), so the panel names a
// failure with the same words.
const ERROR_NOTES = {
  AI_TUTOR_BAD_ATTACHMENT: 'badAttachment',
  AI_TUTOR_SESSION_CLOSED: 'sessionClosed',
  AI_TUTOR_SESSION_NOT_FOUND: 'sessionClosed',
  AI_TUTOR_SUGGESTION_REJECTED: 'staleChoice',
  AI_TUTOR_ASK_REJECTED: 'staleChoice',
  AI_TUTOR_COURSE_NOT_PROVISIONED: 'notProvisioned',
  SYNAPSE_COURSE_MAPPING_MISSING: 'notProvisioned',
  AI_TUTOR_COURSE_HAS_NO_SOURCES: 'notProvisioned',
  AI_TUTOR_MODE_DISABLED: 'rejected',
  // AT-API-020-H1: named only when the refused-action notice is not shown.
  AI_TUTOR_ACTION_REJECTED: 'rejected',
  AI_TUTOR_REQUEST_REJECTED: 'rejected',
  AI_TUTOR_TURN_IN_PROGRESS: 'turnInProgress',
  // P40 QA-22: a turn text over 4,000 characters (422) or a request body
  // over Subway's parser limit (413).
  AI_TUTOR_TEXT_TOO_LONG: 'textTooLong',
  REQUEST_BODY_TOO_LARGE: 'textTooLong',
}

// What the Equation editor says when the board editor refuses a submitted
// source (its typed failure codes; anything else is "not valid").
const EQUATION_PROBLEMS = {
  rtl_text_unsupported: 'rtl',
  engine_unavailable: 'unavailable',
}

// P43-03: a topic that carries explicit math, in any of the delimiters the chat's
// renderer takes. Plain titles never match, so they keep their plain text node.
const TOPIC_MATH = /\$[^$]+\$|\\\([\s\S]+?\\\)|\\\[[\s\S]+?\\\]/

// Below this the chat is a sheet over the board instead of a panel beside it.
const SHEET_QUERY = '(max-width: 1099.98px)'

const BOARD_STATE_KEYS = {
  idle: 'loading',
  loading: 'loading',
  unavailable: 'unavailable',
  not_found: 'notFound',
  forbidden: 'forbidden',
  not_active: 'notActive',
  error: 'error',
}

export default {
  name: 'StudentAiTutorBoardView',
  components: {
    StudentAppHeader,
    TurnList,
    Composer,
    ChatCreditWarning,
    ChatActionRecovery,
    StudentHomeIcon,
    MarkdownContent,
    BoardEditor,
    BoardToolbar,
    BoardEquationDialog,
    BoardShareDialog,
  },
  // 37_VOICE_TO_TEXT (doc 37/00 D-5): the panel composer offers voice like
  // the chat route's; the transcript only fills its draft.
  mixins: [composerVoice],
  data() {
    return {
      // P40 QA-28: the in-page "leave without saving?" question --
      // {resume} replays the navigation that was held, or null.
      leavePrompt: null,
      conversationId: '',
      // The Borna catalog row of this conversation (topic, course, session).
      conversation: null,
      // The decoded persisted scene the editor mounts; frozen so Vue does
      // not walk it. A new scene re-keys the editor.
      initialScene: null,
      editorKey: 0,
      editorReady: false,
      editorFailed: false,
      activeTool: 'freedraw',
      // P41 BT-53: the pen settings of this board (the toolbar shows them, the
      // editor draws with them; they outlive a reloaded editor).
      penStyle: { ...DEFAULT_PEN_STYLE },
      // The editor reported a board with nothing on it (only a reported zero
      // counts).
      boardEmpty: false,
      // The id of the one selected equation, as the editor reports it.
      selectedEquationId: null,
      // The open Equation editor: {mode: 'insert' | 'edit', id, source, busy,
      // problem}, or null. The draft itself lives in the dialog.
      equationDialog: null,
      // WB-M2A: the open share sheet: {share (frozen, or null when there is
      // nothing to share), busy, problem, handedOver}, or null.
      shareDialog: null,
      // The editor is rendering a picture for the sheet.
      shareRendering: false,
      reloading: false,
      // 'loading' | 'ready' | 'unavailable' -- the chat panel only.
      chatStatus: 'loading',
      // The stream is open before the composer is usable -- the chat route's
      // own rule.
      chatReady: false,
      chatOpen: true,
      sheetMode: false,
      unread: false,
    }
  },
  computed: {
    ...mapState('aiTutor', [
      'activeSession',
      'processingState',
      'error',
      'sendingTurn',
      'pendingStudentTurn',
      'feedbackByTurnId',
      'mediaSourceByTurnId',
      'boardShareByTurnId',
      'streamStatus',
    ]),
    ...mapGetters('aiTutor', [
      'awaitingTutorReply',
      'feedbackInFlight',
      // AT-API-020-H1: the refused-action recovery, as on the chat route.
      'actionRecoveryPending',
    ]),
    ...mapState('aiTutorBoard', {
      boardStatus: 'status',
      saveState: 'saveState',
      saveError: 'saveError',
    }),
    locale() {
      return (this.$i18n && this.$i18n.locale) || 'fa'
    },
    // A stale tab cannot edit until it reloads (doc 23 §13.4.8).
    readOnly() {
      return this.saveState === 'stale'
    },
    boardEditable() {
      return this.editorReady && !this.readOnly && !this.editorFailed
    },
    // The empty board's hint: only on a board that can be written on.
    emptyHintShown() {
      return this.boardEditable && this.boardEmpty
    },
    // «ویرایش فرمول» exists while exactly one equation is selected.
    equationEditable() {
      return this.boardEditable && !!this.selectedEquationId
    },
    // WB-M2A: «از AI Tutor بپرس» is usable on a board that can be edited
    // and has something on it, while no sheet is open.
    shareDisabled() {
      return (
        !this.boardEditable ||
        this.boardEmpty ||
        this.shareRendering ||
        !!this.shareDialog ||
        !!this.equationDialog
      )
    },
    // WB-M2A: why the sheet cannot send RIGHT NOW (the chat's own gates,
    // the same ones the composer has), or null. Shown in the sheet; the
    // picture stays.
    // P40 QA-28: offline the reason is named.
    leavePromptText() {
      return isOnline()
        ? this.$t('aiTutorBoard.leaveUnsaved')
        : this.$t('aiTutorBoard.leave.offline')
    },
    shareBlockedBy() {
      // P40 QA-30: a share made offline cannot reach the chat.
      if (!isOnline()) return 'offline'
      if (this.chatStatus !== 'ready' || !this.chatReady) return 'chat'
      if (this.creditsExhausted) return 'credits'
      if (this.sendingTurn || this.feedbackInFlight) return 'busy'
      if (this.readOnly) return 'readOnly'
      return null
    },
    topicLabel() {
      const conversation = this.conversation || {}
      return (
        conversation.topic ||
        conversation.currentNodeTitle ||
        conversation.courseDisplayName ||
        ''
      )
    },
    // P43-03: explicit math only -- `$…$`, `$$…$$`, `\(…\)` or `\[…\]`.
    topicHasMath() {
      return TOPIC_MATH.test(this.topicLabel)
    },
    boardTitle() {
      return this.topicLabel
        ? `${this.$t('aiTutorBoard.title')} · ${this.topicLabel}`
        : this.$t('aiTutorBoard.title')
    },
    boardStateText() {
      return this.$t(
        `aiTutorBoard.states.${BOARD_STATE_KEYS[this.boardStatus] || 'error'}`
      )
    },
    saveLabel() {
      return this.$t(`aiTutorBoard.save.${this.saveState}`)
    },
    notSavedText() {
      const code = this.saveError && this.saveError.code
      if (code === 'AI_TUTOR_BOARD_SCENE_TOO_LARGE') {
        return this.$t('aiTutorBoard.save.tooLarge')
      }
      return this.$t('aiTutorBoard.save.failedHint')
    },
    // CR-03 (doc 28 §16): Subway's balance as the user store holds it.
    aiTutorCreditsRemaining() {
      const credits = this.$store.getters['user/aiTutorCreditsRemaining']
      return typeof credits === 'number' ? credits : null
    },
    // CR-03 (doc 28 §14): only a KNOWN zero gates new chat sends.
    creditsExhausted() {
      const credits = this.aiTutorCreditsRemaining
      return typeof credits === 'number' && credits <= 0
    },
    creditsErrorShownAsWarning() {
      return !!this.error && this.error.kind === 'credits_exhausted'
    },
    turns() {
      const sessionId = this.activeSession && this.activeSession.id
      if (!sessionId) return []
      return this.$store.state.aiTutor.turnsBySessionId[sessionId] || []
    },
    visiblePendingTurn() {
      const pending = this.pendingStudentTurn
      return pending &&
        this.activeSession &&
        pending.sessionId === this.activeSession.id
        ? pending
        : null
    },
    // The chat route's rule, unchanged: at a known zero balance every new
    // chat send is gated. The BOARD is not part of this.
    composerDisabled() {
      return (
        !this.chatReady ||
        this.sendingTurn ||
        this.feedbackInFlight ||
        this.creditsExhausted
      )
    },
    feedbackEnabled() {
      const session = this.activeSession
      return (
        this.chatReady &&
        !this.sendingTurn &&
        !!session &&
        session.status !== 'closed' &&
        !session.pendingAction
      )
    },
    clarifying() {
      if (this.visiblePendingTurn) return false
      const last = this.turns[this.turns.length - 1]
      if (!last || last.role === 'student') return false
      const entry = (this.feedbackByTurnId || {})[last.id]
      return (
        !!entry &&
        entry.kind === 'confused' &&
        entry.status === 'recorded' &&
        !entry.reteachTurnId
      )
    },
    errorNote() {
      const error = this.error || {}
      const key =
        ERROR_NOTES[error.code] || (error.kind === 'network' ? 'network' : '')
      return key
        ? this.$t(`aiTutorSetup.chat.errors.${key}`)
        : this.$t('aiTutorSetup.chat.actionErrorNote')
    },
    studentInitialSource() {
      const userState = this.$store.state.user
      return (userState && userState.user && userState.user.firstname) || ''
    },
  },
  watch: {
    // P40 QA-28: nothing is left to lose once a save went through.
    saveState(now) {
      if (this.leavePrompt && now === 'saved') this.leavePrompt = null
    },
    // Something new in the conversation while it is out of sight. Only once
    // the chat is open for business: the turns the conversation already had
    // when it was loaded are not news.
    'turns.length'(now, before) {
      if (this.chatReady && !this.chatOpen && now > before) this.unread = true
    },
    // A tab that turned stale while the Equation editor is open: nothing can
    // be applied any more (doc 23 §13.4.8); the typed source stays readable.
    readOnly(now) {
      if (now && this.equationDialog) this.equationDialog.problem = 'readOnly'
      // WB-M2A: a stale board blocks the send; the frozen picture stays on
      // screen so the Student sees what would have gone.
      if (now && this.shareDialog) this.shareDialog.problem = 'stale'
    },
  },
  created() {
    this.sheetMedia = null
    this.destroyed = false
    this.initSeq = 0
    this.leftViaRoute = false
    this.flushedForLeave = false
    this.leaveConfirmed = false
  },
  mounted() {
    if (typeof window.matchMedia === 'function') {
      this.sheetMedia = window.matchMedia(SHEET_QUERY)
      this.onSheetMedia(this.sheetMedia)
      this.sheetMedia.addListener(this.onSheetMedia)
    }
    window.addEventListener('pagehide', this.onPageHide)
    // P40 QA-28: a save that failed while offline is tried again as soon as
    // the browser is back online.
    this.offReconnect = onReconnect(this.onReconnect)
    this.init()
    this.loadVoiceCapability()
  },
  // In-app navigation away: the board is flushed first, and the stream is
  // stopped HERE, not in `beforeDestroy` -- the next route (the chat route
  // above all) opens its own stream while it is being created, which is
  // before this view is destroyed.
  async beforeRouteLeave(to, from, next) {
    // A completion action has already flushed (see `leaveConversation`).
    if (
      !this.flushedForLeave &&
      !(await this.flushBeforeLeaving(() => this.replay(to)))
    ) {
      next(false)
      return
    }
    this.leftViaRoute = true
    this.$store.dispatch('aiTutor/stopStreaming')
    // 37_VOICE_TO_TEXT: the microphone is released with the page.
    this.resetVoice()
    next()
  },
  // Another conversation's board on the same route: save this one, then
  // start over for the new id.
  async beforeRouteUpdate(to, from, next) {
    if (!(await this.flushBeforeLeaving(() => this.replay(to)))) {
      next(false)
      return
    }
    this.leaveConfirmed = false
    this.leavePrompt = null
    this.$store.dispatch('aiTutor/stopStreaming')
    next()
    this.$nextTick(() => this.init())
  },
  beforeDestroy() {
    this.destroyed = true
    this.closeShareDialog()
    if (this.sheetMedia) this.sheetMedia.removeListener(this.onSheetMedia)
    window.removeEventListener('pagehide', this.onPageHide)
    if (this.offReconnect) this.offReconnect()
    // Only when the view goes away WITHOUT a route change (never the normal
    // path): a route change has already stopped the stream above, and the
    // next route may have opened its own by now.
    if (!this.leftViaRoute) this.$store.dispatch('aiTutor/stopStreaming')
    this.$store.dispatch('aiTutorBoard/reset')
  },
  methods: {
    init() {
      this.initSeq += 1
      this.conversationId = String(this.$route.params.conversationId || '')
      this.conversation = null
      this.initialScene = null
      this.editorReady = false
      this.editorFailed = false
      this.boardEmpty = false
      this.selectedEquationId = null
      this.equationDialog = null
      this.closeShareDialog()
      this.chatReady = false
      // 37_VOICE_TO_TEXT: a recording or transcript belongs to the
      // conversation it started in.
      this.resetVoice()
      // Two independent loads. Neither waits for the other.
      this.loadBoard()
      this.loadChat()
    },
    // 37_VOICE_TO_TEXT: Subway's capability, from the Reports summary like
    // the chat route reads it. Best-effort and silent: a failure keeps the
    // inert microphone.
    async loadVoiceCapability() {
      let voice = false
      try {
        const api = aiTutorCollaborators.api
        const summary =
          api && typeof api.getReportsSummary === 'function'
            ? await api.getReportsSummary()
            : null
        voice = voiceCapabilityFrom(summary)
      } catch (err) {
        voice = false
      }
      if (!this.destroyed) this.voiceAvailable = voice
    },
    // ---- board ----------------------------------------------------------
    async loadBoard() {
      const seq = this.initSeq
      const result = await this.$store.dispatch(
        'aiTutorBoard/load',
        this.conversationId
      )
      if (this.destroyed || seq !== this.initSeq) return
      if (result && result.ok) {
        this.mountScene(result.scene)
        return
      }
      // The server-driven capability is off: the Workspace does not exist.
      if (this.boardStatus === 'unavailable') this.goToReports(true)
    },
    mountScene(scene) {
      this.initialScene = Object.freeze(scene)
      this.editorReady = false
      this.boardEmpty = false
      // A new editor: nothing is selected in it, and an Equation editor that
      // was open belonged to the old one.
      this.selectedEquationId = null
      this.equationDialog = null
      // WB-M2A: a share sheet open on the old editor's picture is closed too
      // (the picture was of a board that is being replaced).
      this.closeShareDialog()
      this.editorKey += 1
    },
    onEditorReady({ sceneVersion, activeTool, elementCount }) {
      this.editorReady = true
      this.activeTool = activeTool
      this.boardEmpty = elementCount === 0
      // The mounted scene is the saved baseline.
      this.$store.dispatch('aiTutorBoard/markClean', sceneVersion)
    },
    onEditorChange({ sceneVersion, activeTool, elementCount }) {
      this.activeTool = activeTool
      this.boardEmpty = elementCount === 0
      this.$store.dispatch('aiTutorBoard/noteChange', {
        sceneVersion,
        // Read at save time, so the newest scene is what is sent.
        getScene: () => (this.$refs.editor ? this.$refs.editor.getScene() : {}),
      })
    },
    onTool(tool) {
      if (this.$refs.editor) this.$refs.editor.setTool(tool)
    },
    onUndo() {
      if (this.$refs.editor) this.$refs.editor.undo()
    },
    onRedo() {
      if (this.$refs.editor) this.$refs.editor.redo()
    },
    onResetView() {
      if (this.$refs.editor) this.$refs.editor.resetView()
    },
    // P41 BT-53: a colour / width chosen in the toolbar's pen panel.
    onPenStyle(style) {
      this.penStyle = style
    },
    // P41 BT-54: «پاک کردن همهٔ تخته», already confirmed in the toolbar. The
    // editor removes everything as one undoable step; the autosave saves it.
    onClearBoard() {
      if (this.$refs.editor) this.$refs.editor.clearBoard()
    },
    // ---- equation ---------------------------------------------------------
    onEquationSelection(id) {
      this.selectedEquationId = id || null
    },
    // ƒx: the one selected equation is edited; with nothing, several things
    // or something else selected, a new equation is written.
    onEquationTool() {
      const equation = this.equationOf(this.selectedEquationId)
      if (equation) this.openEquationDialog('edit', equation)
      else this.openEquationDialog('insert', null)
    },
    // «ویرایش فرمول», and a double-click on an equation (the editor's
    // `equation-edit`): the same editor, on that equation.
    onEquationEdit(id) {
      const equation = this.equationOf(id)
      if (equation) this.openEquationDialog('edit', equation)
    },
    equationOf(id) {
      const editor = this.$refs.editor
      return id && editor ? editor.getEquation(id) : null
    },
    // Opening the editor changes nothing on the board.
    openEquationDialog(mode, equation) {
      if (!this.boardEditable || this.equationDialog) return
      this.equationDialog = {
        mode,
        id: equation ? equation.id : null,
        source: equation ? equation.latex : '',
        busy: false,
        problem: null,
      }
    },
    // Cancel, Escape, the backdrop: the draft is discarded with the dialog.
    closeEquationDialog() {
      this.equationDialog = null
    },
    onEquationEdited() {
      const dialog = this.equationDialog
      if (!dialog) return
      // A refusal of the source that was submitted; the source has changed.
      // (A missing equation or a read-only board is not about the source.)
      if (dialog.problem !== 'gone' && dialog.problem !== 'readOnly') {
        dialog.problem = null
      }
    },
    // The board editor does the work; the Workspace only reports how it went.
    // The change it makes is saved by the board's autosave like any other.
    async onEquationSubmit(latex) {
      const dialog = this.equationDialog
      const editor = this.$refs.editor
      if (!dialog || dialog.busy) return
      if (!editor || !this.boardEditable) {
        dialog.problem = 'readOnly'
        return
      }
      // EDIT: the equation as it is on the board now. One that was removed
      // while the editor was open is never created again.
      if (dialog.mode === 'edit' && !editor.getEquation(dialog.id)) {
        dialog.problem = 'gone'
        return
      }
      // EDIT with the source it was opened on: nothing was edited. The
      // equation is not touched (the visual field may write the same
      // mathematics differently; that alone is never a change).
      if (dialog.mode === 'edit' && latex === dialog.source) {
        this.equationDialog = null
        this.$nextTick(() => {
          if (this.$refs.editor) this.$refs.editor.focus()
        })
        return
      }
      dialog.busy = true
      let done = null
      let problem = null
      try {
        done =
          dialog.mode === 'edit'
            ? await editor.updateEquation({ id: dialog.id, latex })
            : await editor.insertEquation({ latex })
      } catch (err) {
        problem = EQUATION_PROBLEMS[err && err.code] || 'invalid'
      }
      // Closed, or replaced by another board, while the editor was working.
      if (this.destroyed || this.equationDialog !== dialog) return
      dialog.busy = false
      if (done) {
        this.equationDialog = null
        // The equation is selected; the keyboard goes back to the board.
        this.$nextTick(() => {
          if (this.$refs.editor) this.$refs.editor.focus()
        })
        return
      }
      if (problem) dialog.problem = problem
      else if (dialog.mode === 'edit' && !editor.getEquation(dialog.id)) {
        dialog.problem = 'gone'
      } else dialog.problem = 'readOnly'
    },
    // ---- share (WB-M2A) --------------------------------------------------
    // «از AI Tutor بپرس»: the editor renders the picture of the selection
    // (or of the visible board), the picture is frozen with the board's
    // identity, version and scene version, and the sheet opens on it.
    // Rendering changes nothing on the board.
    async onShareTool() {
      const editor = this.$refs.editor
      if (this.shareDisabled || !editor) return
      this.shareRendering = true
      const seq = this.initSeq
      let rendered = null
      let failed = false
      try {
        rendered = await editor.renderSnapshot()
      } catch (err) {
        failed = true
      }
      if (this.destroyed || seq !== this.initSeq) {
        this.shareRendering = false
        return
      }
      this.shareRendering = false
      if (failed) {
        this.shareDialog = { share: null, busy: false, problem: 'render' }
        return
      }
      if (!rendered) {
        this.shareDialog = { share: null, busy: false, problem: 'empty' }
        return
      }
      const boardState = this.$store.state.aiTutorBoard
      const share = freezeBoardShare({
        scope: rendered.scope,
        elementIds: rendered.elementIds,
        bounds: rendered.bounds,
        selectedCount: rendered.selectedCount,
        blob: rendered.blob,
        width: rendered.width,
        height: rendered.height,
        bytes: rendered.bytes,
        boardId: boardState.boardId,
        boardVersion: boardState.version,
        sceneVersion: rendered.sceneVersion,
        previewUrl: createPreviewUrl(rendered.blob),
      })
      this.shareDialog = {
        share,
        busy: false,
        problem: null,
        handedOver: false,
      }
    },
    // Cancel, Escape, the backdrop, a new board: the picture is discarded
    // unless the chat already owns it (it was sent).
    closeShareDialog() {
      const dialog = this.shareDialog
      if (!dialog) return
      if (dialog.share && dialog.share.previewUrl && !dialog.handedOver) {
        revokePreviewUrl(dialog.share.previewUrl)
      }
      this.shareDialog = null
    },
    // Send: the board is flushed FIRST. A stale board (its save lost the
    // version race) blocks the send -- the existing stale notice and reload
    // are the way out, and the frozen picture stays on screen. A transient
    // save failure does not block: the picture is what the Tutor gets, the
    // board keeps its own NOT SAVED retry, and the share records no board
    // version (null) because none holds this scene yet. The picture is never
    // rendered again here or on a retry.
    async onShareSend({ text, intent }) {
      const dialog = this.shareDialog
      if (!dialog || dialog.busy || !dialog.share || this.shareBlockedBy) return
      dialog.busy = true
      dialog.problem = null
      const seq = this.initSeq
      const saved = await this.$store.dispatch('aiTutorBoard/flush')
      if (
        this.destroyed ||
        seq !== this.initSeq ||
        this.shareDialog !== dialog
      ) {
        return
      }
      if (this.saveState === 'stale') {
        dialog.busy = false
        dialog.problem = 'stale'
        return
      }
      const blocked = this.shareBlockedBy
      if (blocked) {
        dialog.busy = false
        dialog.problem = blocked
        return
      }
      const share = dialog.share
      const message = typeof text === 'string' ? text.trim() : ''
      if (!message) {
        dialog.busy = false
        return
      }
      const boardVersion = saved ? this.$store.state.aiTutorBoard.version : null
      // AT-API-020-H4 (doc 34/01 §3.5, doc 34/02 §3): `boardShareRequest`
      // names the chosen help as its 0.2.0 action (hint -> `solve_exercise`,
      // explain -> `explain_topic`; no chip -> none). The store sends it
      // only when the course's catalog lists it -- otherwise the share
      // travels as text + picture with the same visible message -- and
      // never lets a share carry any other action or inherit the session's
      // Setup action.
      const request = boardShareRequest(intent)
      dialog.handedOver = true
      this.$store.dispatch('aiTutor/sendMessage', {
        text: message,
        image: {
          file: share.blob,
          fileFormat: 'png',
          previewUrl: share.previewUrl,
          source: 'board',
        },
        request: request || undefined,
        boardShare: toBoardShareDescriptor(share, boardVersion),
      })
      this.shareDialog = null
      // P39 TR-017: the share went around the panel's composer, so an error
      // left from an earlier rejected file must not stay above it.
      const composer = this.$refs.composer
      if (composer && typeof composer.clearImageError === 'function') {
        composer.clearImageError()
      }
      // The conversation shows the picture on its way.
      this.openChat()
    },
    onRetrySave() {
      this.$store.dispatch('aiTutorBoard/retrySave')
    },
    // After a stale conflict: the persisted scene replaces the local one.
    async onReload() {
      if (this.reloading) return
      this.reloading = true
      const seq = this.initSeq
      try {
        const result = await this.$store.dispatch('aiTutorBoard/reload')
        if (this.destroyed || seq !== this.initSeq) return
        if (result && result.ok) this.mountScene(result.scene)
      } finally {
        this.reloading = false
      }
    },
    onPageHide() {
      // Best-effort only; never the correctness model (doc 23 §13.4.9).
      this.$store.dispatch('aiTutorBoard/flushOnPageHide')
    },
    // Resolves to whether navigation may proceed. A board that could not be
    // saved is never left silently: P40 QA-28 asks in the page (stay is the
    // default) instead of window.confirm, and `resume` replays the held
    // navigation if the Student chooses to leave without saving.
    async flushBeforeLeaving(resume) {
      if (this.leaveConfirmed) return true
      if (this.leavePrompt) return false
      const saved = await this.$store.dispatch('aiTutorBoard/flush')
      if (saved) return true
      if (!this.$store.getters['aiTutorBoard/hasUnsavedChanges']) return true
      this.leavePrompt = { resume: resume || null }
      this.$nextTick(() => {
        if (this.$refs.leaveStay) this.$refs.leaveStay.focus()
      })
      return false
    },
    onLeaveStay() {
      this.leavePrompt = null
    },
    onLeaveAnyway() {
      const prompt = this.leavePrompt
      this.leavePrompt = null
      this.leaveConfirmed = true
      if (prompt && prompt.resume) prompt.resume()
    },
    // A replay stopped by another guard leaves the Student on the Board: the
    // next leave asks again rather than dropping unsaved work silently.
    replay(to) {
      this.$router.push(to.fullPath).catch(() => {
        this.leaveConfirmed = false
      })
    },
    // Unsaved work (waiting on the retry ladder, given up, or not yet sent)
    // is saved now rather than at the next retry, or never.
    onReconnect() {
      if (this.destroyed) return
      if (['failed', 'retrying', 'dirty'].includes(this.saveState)) {
        this.$store.dispatch('aiTutorBoard/retrySave')
      }
    },
    goToReports(replace) {
      const location = {
        name: 'SessionHistoryView',
        params: { lang: this.$route.params.lang },
      }
      const done = () => undefined
      if (replace) this.$router.replace(location).catch(done)
      else this.$router.push(location).catch(done)
    },
    // ---- chat -----------------------------------------------------------
    // Opens the conversation in the existing `aiTutor` store -- the same
    // `openConversation` Reports uses. Any failure is contained to the panel.
    async loadChat() {
      const seq = this.initSeq
      this.chatStatus = 'loading'
      this.chatReady = false
      let opened = false
      try {
        const conversation = await aiTutorCollaborators.api.getConversation(
          this.conversationId
        )
        if (this.destroyed || seq !== this.initSeq) return
        this.conversation = conversation
        if (
          conversation &&
          conversation.synapseSessionId &&
          conversation.courseName
        ) {
          opened = await this.$store.dispatch('aiTutor/openConversation', {
            synapseSessionId: conversation.synapseSessionId,
            courseName: conversation.courseName,
            intent: conversation.intent,
            grade: conversation.gradeName,
            topic: conversation.topic,
            stuckReason: conversation.stuckReason,
            proficiency: conversation.proficiency,
            // WB-M2A: the Borna share records of this conversation, so a
            // board picture keeps its «از تخته» badge after a reload.
            conversationId: conversation.id,
            boardShares: conversation.boardShares,
          })
        }
      } catch (err) {
        opened = false
      }
      if (this.destroyed || seq !== this.initSeq) return
      if (!opened) {
        // `openSession` leaves its failure in the store's `error`; the panel
        // shows its own state instead of a stale banner.
        this.$store.commit('aiTutor/setError', null)
        this.chatStatus = 'unavailable'
        return
      }
      // One stream for this session, opened before the composer is usable.
      this.$store.dispatch('aiTutor/openTutorStream')
      this.chatReady = true
      this.chatStatus = 'ready'
    },
    onSheetMedia(media) {
      const sheet = !!media.matches
      if (sheet === this.sheetMode) return
      this.sheetMode = sheet
      // Beside the board the conversation is open; over it, it opens on
      // request.
      this.chatOpen = !sheet
    },
    openChat() {
      this.chatOpen = true
      this.unread = false
      this.$nextTick(() => {
        const list = this.$refs.turnList
        if (list && typeof list.follow === 'function') list.follow(true)
      })
    },
    closeChat() {
      this.chatOpen = false
    },
    onComposerSubmit({ text, image }) {
      this.$store.dispatch(
        'aiTutor/sendMessage',
        image ? { text, image } : { text }
      )
    },
    onResend() {
      this.$store.dispatch('aiTutor/retryPendingTurn')
    },
    onPickSuggestion(suggestion) {
      this.$store.dispatch('aiTutor/sendMessage', { suggestion })
    },
    // AT-API-020-H1 (doc 34/01 §3.3): the two choices of the refused-action
    // notice, as on the chat route -- one resend with no action, or nothing.
    onSendWithoutAction() {
      this.$store.dispatch('aiTutor/resendWithoutAction')
    },
    onDismissActionRecovery() {
      this.$store.dispatch('aiTutor/dismissActionRecovery')
    },
    async onFeedback({ turnId, kind }) {
      await this.$store.dispatch('aiTutor/submitFeedback', { turnId, kind })
      if (kind === 'confused' && this.clarifying && this.$refs.composer) {
        this.$refs.composer.focus()
      }
    },
    onDismissFeedback(turnId) {
      this.$store.dispatch('aiTutor/dismissFeedback', turnId)
    },
    onMediaExpired(turnId) {
      this.$store.dispatch('aiTutor/refreshExpiredMedia', turnId)
    },
    // V405-F1's completion actions, as the chat route performs them: they
    // clear only the open conversation with the same store action (no API
    // call, nothing deleted -- the conversation and its board stay in
    // Reports) and leave the Workspace. The board is flushed FIRST, so a
    // board that cannot be saved keeps the student here with the
    // conversation still open.
    onEndConversation() {
      return this.leaveConversation('DashboardView')
    },
    onNewQuestion() {
      return this.leaveConversation('StudentAiTutorView')
    },
    async leaveConversation(routeName) {
      if (
        !(await this.flushBeforeLeaving(() =>
          this.leaveConversation(routeName)
        ))
      ) {
        return
      }
      this.flushedForLeave = true
      this.$store.dispatch('aiTutor/beginNewConversation')
      this.$router
        .push({ name: routeName, params: { lang: this.$route.params.lang } })
        .catch(() => undefined)
    },
  },
}
</script>

<style lang="scss" scoped>
$board-gap: 16px;
$board-pad-x: 24px;
$board-bar: 56px;

// The Workspace fills what the accepted shell leaves: the viewport below the
// sticky `student-app-header`, and above the mobile bottom navigation.
.ai-tutor-board-view {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  background: $borna-canvas;
  color: $borna-text;
  font-family: $borna-font-family;
  font-size: 14px;
  line-height: 1.75;
}

// Board : chat = 2 : 1 beside each other (DESIGN-21's functional layout).
// The conversation is never narrower than it can be read in (S6): on a
// narrow desktop the board gives the room.
.ai-tutor-board {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  gap: $board-gap;
  min-height: 0;
  padding: 16px $board-pad-x;
}

.ai-tutor-board--chat-collapsed {
  grid-template-columns: minmax(0, 1fr);
}

.ai-tutor-board__board,
.ai-tutor-board__chat {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid $borna-border;
  border-radius: 20px;
  background: $borna-surface;
  box-shadow: $borna-shadow-card;
}

.ai-tutor-board__bar {
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;
  min-height: $board-bar;
  padding: 8px 12px;
  border-bottom: 1px solid $borna-border;
}

.ai-tutor-board__icon-button {
  display: grid;
  place-items: center;
  flex: none;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid $borna-border;
  border-radius: 11px;
  background: $borna-surface;
  color: $borna-muted;
  cursor: pointer;
}

// P43-02: the back arrow is «→» in Persian and «←» in English (`arrow` is
// drawn «←», so the Persian locale mirrors it).
html.is-rtl .ai-tutor-board__bar .ai-tutor-board__icon-button ::v-deep svg {
  transform: scaleX(-1);
}

.ai-tutor-board__title {
  flex: 1;
  // Squeezed (a phone, a long save state): the word «تخته» stays.
  min-width: 44px;
  margin: 0;
  overflow: hidden;
  color: $borna-ink;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.6;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// A long state («ذخیره نشد؛ دوباره تلاش می‌کنیم…») on a phone is cut inside the
// bar; it never pushes the chat opener out of the card.
.ai-tutor-board__save {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 3px 10px;
  border-radius: $borna-radius-pill;
  background: $borna-canvas;
  color: $borna-muted;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;

  &--saved {
    background: $borna-teal-50;
    color: $borna-teal-700;
  }

  &--failed,
  &--stale {
    background: $borna-error-bg;
    color: $borna-error-text;
  }
}

.ai-tutor-board__chat-toggle {
  position: relative;
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid $borna-indigo-100;
  border-radius: 12px;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.ai-tutor-board__unread {
  position: absolute;
  top: 6px;
  inset-inline-end: 7px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e5484d;
}

.ai-tutor-board__canvas {
  position: relative;
  flex: 1;
  min-height: 0;
}

.ai-tutor-board__empty {
  position: absolute;
  inset: 0;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: 6px;
  padding: 24px;
  color: $borna-muted;
  font-size: 13px;
  text-align: center;
  pointer-events: none;

  strong {
    color: $borna-ink;
    font-size: 17px;
    font-weight: 500;
  }
}

.ai-tutor-board__foot {
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 14px;
  border-top: 1px solid $borna-border;
  color: $borna-muted;
  font-size: 11px;
}

.ai-tutor-board__notice {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: $borna-error-text;
  font-weight: 700;
}

.ai-tutor-board__notice--leave {
  flex-wrap: wrap;

  > span {
    flex: 1 1 12em;
  }
}

// «ویرایش فرمول»: at the end of the footer, whatever else it shows. It
// comes and goes with the selection, so it must not change the footer's
// height (the board above it would be resized each time): it reaches into
// the footer's own padding instead.
.ai-tutor-board__equation-edit {
  margin-block: -6px;
  margin-inline-start: auto;
}

.ai-tutor-board__button {
  flex: none;
  height: 32px;
  padding: 0 14px;
  border: 1px solid $borna-indigo-100;
  border-radius: 10px;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font-family: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.ai-tutor-board__state,
.ai-tutor-board__chat-state {
  display: grid;
  flex: 1;
  place-content: center;
  justify-items: center;
  gap: 12px;
  padding: 24px;
  color: $borna-muted;
  font-size: 13px;
  text-align: center;

  p {
    margin: 0;
  }
}

.ai-tutor-board__chat-head {
  display: flex;
  flex: none;
  align-items: center;
  gap: 9px;
  min-height: $board-bar;
  padding: 8px 12px;
  border-bottom: 1px solid $borna-border;
}

.ai-tutor-board__chat-chip {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: $borna-radius-pill;
  background: $borna-indigo-50;
  color: $borna-indigo-900;
  font-size: 10px;
  font-weight: 800;
}

.ai-tutor-board__chat-topic {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: $borna-ink;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// P43-03: the title and the chat's topic are ONE line that ends in an ellipsis.
// The inline renderer keeps a formula breakable (a suggestion card wraps its
// label); here neither the text nor the formula may wrap.
.ai-tutor-board__title,
.ai-tutor-board__chat-topic {
  ::v-deep .ai-md--inline {
    overflow-wrap: normal;
    white-space: nowrap;
  }

  ::v-deep .ai-md--inline .ai-md__math {
    white-space: nowrap;
  }
}

// The scroll container `TurnList` follows (it finds its nearest scrollable
// ancestor on its own).
.ai-tutor-board__chat-scroll {
  flex: 1;
  min-height: 0;
  padding: 14px 12px 6px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
}

// The chat cards switch to their own phone layout by VIEWPORT width (their
// `max-width: 620px` rules in ChatFeedback.vue / ChatSuggestions.vue). A panel
// a third of a desktop viewport is narrower than that, so exactly those
// rules are applied here by container. Layout only; the components are not
// modified and their rules on the chat route are untouched.
.ai-tutor-board__chat-scroll ::v-deep {
  .ai-tutor-suggestions {
    margin-inline: 0;
  }

  .ai-tutor-suggestions__cards {
    grid-template-columns: 1fr;

    button {
      min-height: 68px;
    }
  }

  .ai-tutor-feedback {
    flex-direction: column;
    align-items: flex-start;
    margin: 2px 0 0;
  }

  .ai-tutor-feedback__actions {
    width: 100%;

    button {
      flex: 1;
    }
  }

  .ai-tutor-feedback-recovery {
    margin: 0;
    padding: 14px;
  }
}

.ai-tutor-board__chat-error {
  flex: none;
  margin: 0 12px 8px;
  padding: 8px 12px;
  border: 1px solid $borna-error-border;
  border-radius: 12px;
  background: $borna-error-bg;
  color: $borna-error-text;
  font-size: 11px;
}

.ai-tutor-board__composer {
  flex: none;
  padding: 8px 10px 10px;
}

.ai-tutor-board__sheet-backdrop {
  display: none;
}

// ---------------------------------------------------------------------------
// SHEET MODE -- board primary, the conversation a sheet over it. The board
// stays mounted underneath. The Student bottom navigation stays where the
// shell puts it (no immersive mode in M1A).
// ---------------------------------------------------------------------------
@media (max-width: 1099.98px) {
  .ai-tutor-board {
    grid-template-columns: minmax(0, 1fr);
    padding: 8px 10px;
  }

  .ai-tutor-board__board {
    border-radius: 18px;
  }

  .ai-tutor-board__foot {
    min-height: 36px;
    font-size: 10px;
  }

  .ai-tutor-board__sheet-backdrop {
    position: fixed;
    inset: 0;
    z-index: 60;
    display: block;
    padding: 0;
    border: 0;
    background: rgba(7, 20, 71, 0.34);
  }

  .ai-tutor-board__chat {
    position: fixed;
    top: 96px;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 61;
    border-width: 1px 0 0;
    border-radius: 24px 24px 0 0;
    box-shadow: 0 -18px 50px rgba(7, 20, 71, 0.22);
  }
}

// The mobile shell: the bottom navigation is fixed at the floor, so the
// Workspace ends above it and the chat sheet stops above it too.
@include borna-mobile {
  .ai-tutor-board-view {
    height: calc(
      100vh - #{$borna-bottom-nav-clearance} - env(safe-area-inset-bottom)
    );
    height: calc(
      100dvh - #{$borna-bottom-nav-clearance} - env(safe-area-inset-bottom)
    );
  }

  .ai-tutor-board__chat {
    bottom: calc(#{$borna-bottom-nav-clearance} + env(safe-area-inset-bottom));
  }
}
</style>
